/**
 * VIVA HUB app registry and permission helpers
 */

import {
  hasPermission as hasHubPermission,
  isHubSuperAdmin,
  isSchedulerAdmin,
} from './hub-groups.js'

/**
 * CRM is temporarily disabled in the HUB UI (dashboard card shown as disabled, routes redirect to /hub).
 * Backend APIs and data are untouched. Flip to true to re-enable.
 */
export const CRM_ENABLED = false

export const HUB_APP_PERMISSIONS = {
  room_scheduler: 'access_room_scheduler',
  audit_log: 'access_audit_log',
  contact: 'access_contact',
  client_intake: 'access_client_intake',
  crm: 'access_crm',
  whatsapp: 'access_whatsapp',
  settings: 'access_settings',
  groups: 'access_groups',
  profile: 'access_profile',
  email_signature: 'access_email_signature',
}

export const SCHEDULER_PERMISSIONS = [
  'manage_rooms',
  'manage_users',
  'manage_groups',
  'view_reports',
  'manage_reservations',
]

export const ALL_PERMISSIONS = [
  ...Object.values(HUB_APP_PERMISSIONS),
  ...SCHEDULER_PERMISSIONS,
]

export const HUB_APP_LABELS = {
  viva_hub: 'VIVA HUB',
  room_scheduler: 'Room Scheduler',
  contact: 'Contact',
  client_intake: 'Client Intake',
  crm: 'CRM',
  whatsapp: 'WhatsApp',
  settings: 'Settings',
  groups: 'Groups',
  profile: 'Profile',
  email_signature: 'Email Signature',
}

export const HUB_APPS = [
  {
    id: 'room_scheduler',
    name: 'Room Scheduler',
    description: 'Manage room reservations, billing, and calendar',
    permission: HUB_APP_PERMISSIONS.room_scheduler,
    path: '/scheduler/calendar',
    adminPath: '/scheduler',
  },
  {
    id: 'profile',
    name: 'Profile',
    description: 'Manage your account, password, and contact information',
    permission: HUB_APP_PERMISSIONS.profile,
    path: '/hub/profile',
  },
  {
    id: 'email_signature',
    name: 'Email Signature',
    description: 'Build a VIVA-branded signature and copy it into Gmail, Outlook, or Apple Mail',
    permission: HUB_APP_PERMISSIONS.email_signature,
    path: '/hub/email-signature',
  },
  {
    id: 'audit_log',
    name: 'Audit Log',
    description: 'View system activity and audit trail across all apps',
    permission: HUB_APP_PERMISSIONS.audit_log,
    path: '/hub/audit-log',
  },
  {
    id: 'crm',
    name: 'CRM',
    description: 'Prospective client cases, timeline, and intake workflow',
    permission: HUB_APP_PERMISSIONS.crm,
    path: '/hub/crm',
    disabled: !CRM_ENABLED,
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    description: 'Inbound Meta WhatsApp webhook messages, auto-replies, and delivery status',
    permission: HUB_APP_PERMISSIONS.whatsapp,
    path: '/hub/whatsapp',
  },
  {
    id: 'settings',
    name: 'Users',
    description: 'Create and manage user accounts across VIVA HUB',
    permission: HUB_APP_PERMISSIONS.settings,
    path: '/hub/settings',
  },
  {
    id: 'groups',
    name: 'Groups',
    description: 'Manage groups and RBAC permissions for all apps',
    permission: HUB_APP_PERMISSIONS.groups,
    path: '/hub/groups',
  },
]

export function hasPermission(user, permission) {
  if (Array.isArray(permission)) {
    return permission.some((p) => hasHubPermission(user, p))
  }
  return hasHubPermission(user, permission)
}

export function getAccessibleApps(user) {
  if (!user) return []
  return HUB_APPS.filter((app) => hasPermission(user, app.permission))
}

export function getAppPath(app, user) {
  if (app.id === 'room_scheduler' && isSchedulerAdmin(user)) {
    return app.adminPath
  }
  return app.path
}

export { isSchedulerAdmin, isHubSuperAdmin } from './hub-groups.js'
